import React, { memo, useMemo, useEffect, useCallback, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { includes, find, pathEq, propEq } from 'ramda';
import { Box } from '@mui/material';
import { SearchField } from '@alycecom/ui';

import {
  GIFT_TYPES_FILTER,
  CATEGORIES_FILTER,
  BRANDS_FILTER,
  SORTING,
  SEARCH_FILTER_OPTIONS,
} from '../../constants/filters.constants';
import CloseableTab from '../../../../components/Shared/CloseableTab';
import ConfirmStepButton from '../../../../components/Shared/ConfirmStepButton';
import { FilterShape, FilterItemShape, FilterChipShape } from '../../shapes';
import { getSortingItems } from '../../constants/sorting.constants';

import FilterItemsList from './MobileFilterItemsList';
import FilterItemsRadioList from './MobileFilterItemsRadioList';
import { setSearch, setSorting, setFilter, toggleTab, addChip, deleteChip } from './store/filters.actions';
import filtersReducer, { initialState } from './store/filters.reducer';

const MobileFilters = ({
  isDonation,
  isDeclaredDataEnabled,
  hasCustomMarketplace,
  sort,
  search,
  isSearchEnabled,
  filters,
  filterChips,
  giftTypesFilter,
  categoriesFilter,
  brandsFilter,
  onApply,
  isWhiteLabeling,
}) => {
  const sortingItems = useMemo(
    () => getSortingItems({ isDeclaredDataEnabled, hasCustomMarketplace, isWhiteLabeling }),
    [isDeclaredDataEnabled, hasCustomMarketplace, isWhiteLabeling],
  );
  const sorting = useMemo(() => find(pathEq(['value', 'column'], sort.value.column), sortingItems), [
    sort,
    sortingItems,
  ]);
  const componentJustMount = useRef(true);

  const [state, dispatch] = useReducer(filtersReducer, {
    ...initialState,
    filters,
    search,
    filterChips,
    sort: sorting.id,
  });
  const { opened, sort: sortValue, search: searchValue, filters: filtersValue } = state;

  const filtersOptions = useMemo(
    () => ({
      [GIFT_TYPES_FILTER.key]: giftTypesFilter,
      [CATEGORIES_FILTER.key]: categoriesFilter,
      [BRANDS_FILTER.key]: brandsFilter,
    }),
    [giftTypesFilter, categoriesFilter, brandsFilter],
  );
  const tabBorder = { width: 2, color: 'customStyling.primaryColor' };

  const getIsTabOpened = useCallback(key => includes(key, opened), [opened]);
  const getTabTitle = useCallback(
    (key, label) => {
      const filterItemsCount = filtersValue[key].length;
      return filterItemsCount > 0 ? `${label} (${filterItemsCount})` : label;
    },
    [filtersValue],
  );

  const handleSort = useCallback(value => dispatch(setSorting(parseInt(value, 10))), []);
  const handleSearch = useCallback(({ target }) => {
    dispatch(setSearch(target.value));
    dispatch(deleteChip({ ...SEARCH_FILTER_OPTIONS }));
    if (target.value) {
      dispatch(addChip({ ...SEARCH_FILTER_OPTIONS, label: target.value }));
    }
  }, []);

  const handleOpen = useCallback(
    (name, isOpen) => {
      dispatch(toggleTab({ isOpen, name }));
      const filter = filtersOptions[name];
      if (filter && !isOpen && !filter.isLoading) {
        filter.openFilter();
      }
    },
    [filtersOptions],
  );

  const handleChange = useCallback((name, selectedValues, option, checked) => {
    dispatch(setFilter({ key: name, items: selectedValues }));
    const chipAction = checked ? addChip : deleteChip;
    dispatch(chipAction({ name, ...option }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    const sortItem = find(propEq('id', state.sort), sortingItems);
    onApply({ ...state, sort: { id: state.sort, value: sortItem.value } });
  }, [onApply, state, sortingItems]);

  useEffect(() => {
    const { current: mount } = componentJustMount;
    if (!isDonation && mount) {
      const filter = filtersOptions[GIFT_TYPES_FILTER.key];
      filter.openFilter();
      componentJustMount.current = false;
    }
  }, [isDonation, componentJustMount, filtersOptions]);

  return (
    <>
      <Box px={3} pt={4.5} mb={10} minHeight="calc(100% - 80px)">
        {isSearchEnabled && (
          <SearchField
            placeholder="Search filter"
            value={searchValue}
            closeIconColor="customStyling.highlightColor"
            onChange={handleSearch}
          />
        )}
        {!isDonation && (
          <CloseableTab
            name={GIFT_TYPES_FILTER.key}
            title={getTabTitle(GIFT_TYPES_FILTER.key, GIFT_TYPES_FILTER.label)}
            isOpen={getIsTabOpened(GIFT_TYPES_FILTER.key)}
            border={tabBorder}
            toggleOpen={handleOpen}
          >
            <FilterItemsList
              name={GIFT_TYPES_FILTER.key}
              label={GIFT_TYPES_FILTER.label}
              value={filtersValue[GIFT_TYPES_FILTER.key]}
              withSearch={false}
              isLoading={giftTypesFilter.isLoading}
              options={giftTypesFilter.options}
              onChange={handleChange}
            />
          </CloseableTab>
        )}
        <CloseableTab
          name={CATEGORIES_FILTER.key}
          title={getTabTitle(CATEGORIES_FILTER.key, CATEGORIES_FILTER.label)}
          isOpen={getIsTabOpened(CATEGORIES_FILTER.key)}
          border={tabBorder}
          toggleOpen={handleOpen}
        >
          <FilterItemsList
            name={CATEGORIES_FILTER.key}
            label={CATEGORIES_FILTER.label}
            value={filtersValue[CATEGORIES_FILTER.key]}
            isLoading={categoriesFilter.isLoading}
            options={categoriesFilter.options}
            onChange={handleChange}
          />
        </CloseableTab>
        <CloseableTab
          name={BRANDS_FILTER.key}
          title={getTabTitle(BRANDS_FILTER.key, BRANDS_FILTER.label)}
          isOpen={getIsTabOpened(BRANDS_FILTER.key)}
          border={tabBorder}
          toggleOpen={handleOpen}
        >
          <FilterItemsList
            name={BRANDS_FILTER.key}
            label={BRANDS_FILTER.label}
            value={filtersValue[BRANDS_FILTER.key]}
            isLoading={brandsFilter.isLoading}
            options={brandsFilter.options}
            onChange={handleChange}
          />
        </CloseableTab>
        <CloseableTab
          name={SORTING.key}
          title="Sort by"
          isOpen={getIsTabOpened(SORTING.key)}
          border={tabBorder}
          toggleOpen={handleOpen}
        >
          <FilterItemsRadioList name={SORTING.key} value={sortValue} options={sortingItems} onChange={handleSort} />
        </CloseableTab>
      </Box>
      <ConfirmStepButton py={2} width={['100%', 400]} text="Apply filters" onClick={handleApplyFilters} alwaysMobile />
    </>
  );
};

MobileFilters.propTypes = {
  isDonation: PropTypes.bool.isRequired,
  isDeclaredDataEnabled: PropTypes.bool,
  hasCustomMarketplace: PropTypes.bool,
  search: PropTypes.string.isRequired,
  isSearchEnabled: PropTypes.bool,
  isWhiteLabeling: PropTypes.bool,
  sort: PropTypes.shape({
    id: PropTypes.number.isRequired,
    value: PropTypes.shape({
      column: PropTypes.string.isRequired,
      order: PropTypes.string.isRequired,
    }),
  }).isRequired,
  filters: PropTypes.shape({
    giftTypes: PropTypes.arrayOf(FilterItemShape).isRequired,
    categories: PropTypes.arrayOf(FilterItemShape).isRequired,
    brands: PropTypes.arrayOf(FilterItemShape).isRequired,
  }).isRequired,
  giftTypesFilter: FilterShape.isRequired,
  categoriesFilter: FilterShape.isRequired,
  brandsFilter: FilterShape.isRequired,
  filterChips: PropTypes.arrayOf(FilterChipShape).isRequired,
  onApply: PropTypes.func.isRequired,
};

MobileFilters.defaultProps = {
  isSearchEnabled: true,
  isDeclaredDataEnabled: false,
  hasCustomMarketplace: false,
  isWhiteLabeling: false,
};

export default memo(MobileFilters);
